import { Transition } from "@headlessui/react";
import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import React, {
    useEffect,
    useContext,
    useReducer,
    useCallback,
    useState,
} from "react";
import { fromSqlDateTime } from "helpers/Date";
import { useAuth } from "hooks/Auth";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { apiGet, apiPost, apiPut, messageFromException } from "helpers/Request";
import {
    transform as contentTransformer,
    ContentObjectSchema,
    prepare as contentPreparer,
} from "schemas/Content";
import { transform as copyReferenceTransformer } from "schemas/CopyReference";
import EditForm from "./EditForm";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { transform } from "lodash";
import { ContentContext } from "context/Content";
import { AppContext } from "context/App";
import { toast } from "react-hot-toast";
import ArticleScreenErrorBoundary from "./ArticleScreenErrorBoundary";
import { addItem as addRecentItem } from "redux/actions/recent-items";
import { modal } from "components/HeadlessModal";
import DangerButton from "components/Buttons/DangerButton";
import * as KVStore from "helpers/KVStore";
import striptags from "striptags";

const ensureContentShape = (content, contentFlow) => {
    if (content.hasOwnProperty("revived_from_cache")) {
        return content;
    }

    const allProps = contentFlow.metaProperties["content"].concat(
        contentFlow.metaProperties["content-lower"]
    );
    return Object.assign({}, content, {
        meta: transform(
            allProps,
            (result, metaProp, key) => {
                const val = content.metaProperties.find(
                    (mp) => mp.field === metaProp.field
                );
                result[metaProp.field] = val ? val.value : "";
            },
            {}
        ),
        tags: content.tags.map((tag) => {
            return { label: tag.name, value: tag.uuid };
        }),
        authors: content.authors.map((author) => {
            return { label: author.name, value: author.uuid };
        }),
        workflow: content.workflow
            ? {
                  value: content.workflow.id,
                  label: content.workflow.name,
              }
            : {
                  value: contentFlow.workflows[0].id,
                  label: contentFlow.workflows[0].name,
              },
        type: "article",
        custom_url: "",
    });
};

const showSaveErrorModal = (err) => {
    modal.error({
        title: "Unable to save article",
        children: messageFromException(err),
    });
};

const showLoadErrorModal = (history, err) => {
    modal.error({
        title: "Unable to load article",
        children: messageFromException(err),
        buttons: () => (
            <DangerButton
                onClick={() => {
                    history.push("/app/dashboard");
                    modal.dismiss();
                }}
            >
                Try again
            </DangerButton>
        ),
    });
};

const reducer = (oldState, newState) => {
    return Object.assign({}, oldState, newState);
};

const loadContent = async (uuid, pub, setState) => {
    setState({
        loading: true,
        content: ContentObjectSchema,
    });

    return await apiGet("/v4/content/" + uuid, pub, {
        include: "dominantAttachment,urls",
    })
        .then(async (response) => {
            let item = contentTransformer(response);
            if (window?.location.search) {
                const q = new URLSearchParams(window.location.search);
                if (q.has("recovery")) {
                    const content = await KVStore.getBang(item.uuid);
                    if (!content) {
                        return item;
                    }
                    item = Object.assign({}, item, {
                        content: content,
                    });
                    KVStore.remove(item.uuid);
                }
            }
            return item;
        })
        .then((item) => {
            setState({ content: item, loading: false });
            return item;
        })
        .catch((err) => {
            setState({
                content: ContentObjectSchema,
                loading: false,
                isUserEditable: false,
            });
            throw err;
        });
};

const prepareForSave = (values, content, contentFlow) => {
    const allProps = contentFlow.metaProperties["content"].concat(
        contentFlow.metaProperties["content-lower"]
    );

    const tags = values.tags.map((tag) => tag.value);
    const authors = values.authors.map((author) => author.value);

    const meta = Object.keys(values.meta)
        .map((key, i) => {
            const type = allProps.find((mp) => mp.field === key);
            return {
                field: key,
                value: values.meta[key],
                type: type ? type.type : "NoopMetaProperty",
            };
        })
        .filter((mp) => mp.value !== "");

    const domAttachment =
        values.dominantAttachment && values.dominantAttachment.uuid
            ? values.dominantAttachment.uuid
            : null;

    return contentPreparer(
        Object.assign({}, content, values, {
            content_raw: "",
            abstract_raw: "",
            tags: tags,
            authors: authors,
            meta: meta,
            dominant_attachment: domAttachment,
            workflow_id: values.workflow.value,
            urls: values.custom_url
                ? [{ url: values.custom_url, is_default: 1 }]
                : undefined,
            layout_template:
                values.layout_template === "Default"
                    ? null
                    : values.layout_template,
        })
    );
};
const linkify = function (string) {
    return string;
};
const setRefValue = function (value) {
    let str = value.replace(/(?:\r\n|\r|\n)/g, "<br />");
    str = linkify(str);

    return { __html: striptags(str, ["a", "br"]) };
};

function ArticlePage(props) {
    const { uuid } = useParams();
    const location = useLocation();
    const auth = useAuth();
    const dispatch = useDispatch();
    const history = useHistory();
    const [state, setState] = useReducer(reducer, {
        content: ContentObjectSchema,
        formValues: ContentObjectSchema,
        loading: false,
        saving: false,
        isUserEditable: false,
        performingStateUpdate: false,
        requestReload: useCallback(() => {
            loadContent(uuid, auth.publication, setState).catch((err) => {
                showLoadErrorModal(history, err);
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [uuid, auth.publication]),
    });
    const { setCurrentNav } = useContext(AppContext);

    const [currentNote, setCurrentNote] = useState(null);
    const [showCopyNote, setShowCopyNote] = useState(false);

    const contentFlow = useSelector((state) => state.contentFlow);

    // const kvStore = useMemo(() => {
    //     return new IdbKvStore("contentCache");
    // }, []);

    // const handleRequestReload = () => {
    //     setState((state) => state + 1);
    // };

    const onSubmit = (values) => {
        if (
            state.content.lock &&
            state.content.lock.user.uuid !== auth.user.uuid
        ) {
            console.log("not locked by user");
            return;
        } else if (state.content.uuid && !state.content.lock) {
            console.log("no content lock");
            return;
        }

        const data = prepareForSave(values, state.content, contentFlow);
        setState({ saving: true });
        const toastId = toast.loading("Saving...");
        const apiAction = state.content.uuid ? apiPut : apiPost;

        return apiAction(
            "/v4/content/" +
                (state.content.uuid ? state.content.uuid : "") +
                "?include=dominantAttachment,urls",
            auth.publication,
            data
        )
            .then((resp) => {
                const item = contentTransformer(resp);
                toast.success("Saved!", { id: toastId });
                if (!state.content.uuid) {
                    // was a new item, so redirect
                    apiPost("/v4/lock-request", auth.publication, {
                        srn: item.srn,
                    })
                        .then((resp) => {
                            history.push(item.edit_url);
                        })
                        .catch((err) => {
                            toast.error("Unable to acquire lock", {
                                id: toastId,
                            });
                        });
                } else {
                    KVStore.remove(state.content.uuid);
                }

                setState({ content: item, saving: false, loading: false });

                // return releaseLockOnSave;
            })
            .catch((err) => {
                setState({ saving: false });
                toast.dismiss(toastId);
                showSaveErrorModal(err);
            });
    };

    const onRequestNavigate = (url) => {
        history.push(url);
    };

    const onCreateNewCopyRef = async (ref) => {
        toast.loading("Saving reference...");
        const resp = await apiPost(
            "/v4/content/" + state.content.uuid + "/ref",
            auth.publication,
            {
                value: ref,
            }
        );
        const copyRef = copyReferenceTransformer(resp);
        toast.dismiss();
        toast.success("Reference saved!");
        return copyRef.uuid;
    };

    const onRequestLoadCopyRef = async (uuid, target) => {
        setCurrentNote(null);
        setShowCopyNote(false);
        const toastId = toast.loading("Loading reference...");
        const resp = await apiGet(
            "/v4/content/" +
                state.content.uuid +
                "/ref/" +
                uuid +
                "?include=children",
            auth.publication
        );
        const copyRef = copyReferenceTransformer(resp);
        toast.dismiss(toastId);
        setCurrentNote(
            <div>
                <div className="mb-4 text-xs font-light">
                    {copyRef.user.name} ({copyRef.user.email}) -{" "}
                    {fromSqlDateTime(copyRef.created_at).toFormat("ff")}
                </div>
                <div
                    className=" prose-sm overflow-auto text-gray-900"
                    dangerouslySetInnerHTML={setRefValue(copyRef.value)}
                ></div>
            </div>
        );
        setShowCopyNote(true);

        // modal({
        //     children: (
        //         <div>
        //             <div className="mb-4 text-xs font-light">
        //                 {copyRef.user.name} ({copyRef.user.email}) -{" "}
        //                 {fromSqlDateTime(copyRef.created_at).toFormat("ff")}
        //             </div>
        //             <div
        //                 className=" prose-sm overflow-auto text-gray-900"
        //                 dangerouslySetInnerHTML={setRefValue(copyRef.value)}
        //             ></div>
        //         </div>
        //     ),
        //     buttons: ({ disabled }) => (
        //         <PrimaryButton onClick={modal.dismiss} disabled={disabled}>
        //             Dismiss
        //         </PrimaryButton>
        //     ),
        // });
    };

    useEffect(() => {
        if (!uuid || uuid === "new") {
            setState({
                loading: false,
                content: ContentObjectSchema,
            });
            return;
        }

        loadContent(uuid, auth.publication, setState)
            .then((item) => {
                dispatch(addRecentItem(item));
            })
            .catch((err) => {
                showLoadErrorModal(history, err);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, uuid, auth.publication, dispatch]);

    useEffect(() => {
        setCurrentNav("content");
    }, [setCurrentNav]);

    useEffect(() => {
        // fixes a bug where the console notes a component updating another, unmounted
        // component. just delay the update until the next tick
        setTimeout(() => {
            setState({
                formValues: ensureContentShape(state.content, contentFlow),
            });
        });
    }, [state.content, contentFlow]);

    useEffect(() => {
        if (
            state.content.uuid &&
            state.content.lock &&
            state.content.lock.user.uuid === auth.user.uuid
        ) {
            setState({ isUserEditable: true });
        } else if (!state.content.uuid) {
            setState({ isUserEditable: true });
        } else {
            setState({ isUserEditable: false });
        }
    }, [state.content, auth.user]);

    // content and form values are provided via context provider
    return (
        <ContentContext.Provider
            value={{
                state,
                setState,
                user: auth.user,
            }}
        >
            <MainBlock className="z-0">
                <ArticleScreenErrorBoundary>
                    <EditForm
                        onSubmit={onSubmit}
                        onRequestNavigate={onRequestNavigate}
                        onCreateNewCopyRef={onCreateNewCopyRef}
                        onRequestLoadCopyRef={onRequestLoadCopyRef}
                    />
                </ArticleScreenErrorBoundary>
                <Transition
                    show={showCopyNote}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div
                        className="w-sm fixed inset-x-auto top-10 mx-auto"
                        style={{ zIndex: 99999 }}
                    >
                        <div
                            className={[
                                "z-50 rounded bg-yellow-200 p-4 text-black shadow-md",
                            ]}
                        >
                            {currentNote}
                            <div className="w-100 flex justify-end">
                                <button
                                    onClick={() => setShowCopyNote(false)}
                                    type="button"
                                    className="rounded border border-yellow-700 py-1 px-3 text-sm text-yellow-800"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </Transition>
            </MainBlock>
        </ContentContext.Provider>
    );
}

export default ArticlePage;
